// Las PREFERENCIAS de generación del usuario: QUÉ está por hacer (imagen o video) y con
// qué formato, calidad y duración lo genera el agente por defecto, salvo que pida otra
// cosa en el mensaje — ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas y
// ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas.
//
// Viven en `localStorage` como los interruptores del chat (mismo criterio: son de este
// navegador y de esta cuenta; el corte por cuenta lo hace `sesionDelChat`), y llegan a
// tres lugares: el bloque volátil del system prompt (para que el modelo las asuma), las
// cards de generación (para que preseleccionen lo mismo) y el pedido de imagen (la
// resolución viaja como `image_size`). `null` en un campo = sin preferencia: el agente
// decide como siempre (último formato usado, calidad base, video profesional).
//
// Todo puro: el storage se inyecta, así se testea con un Map.

import { FORMAT_OPTIONS_V2 } from '../pages/v2/mockData';
import { normalizarResolucion, RESOLUCIONES, type ResolucionImagen } from '../lib/resolucionImagen';
import {
  VIDEO_BASICA_MAX_S,
  VIDEO_PROFESIONAL_MAX_S,
  videoGeneralRatePerSec,
  type VideoResolution,
} from '../utils/credits';
import { RESOLUCIONES_VIDEO as LISTA_DE_RESOLUCIONES, detalleDeResolucion } from '../lib/resolucionVideo';
import type { StorageLike } from './interruptoresChat';

export type CalidadVideo = 'basica' | 'profesional';

/**
 * Qué está por generar el usuario, en las TRES posiciones del switch de la caja
 * (ADR-2026-09-22-el-switch-arranca-en-agente, que ajusta
 * ADR-2026-09-21-el-switch-de-imagen-o-video):
 *
 *   • `agente` — el usuario no eligió: decide el agente leyendo el pedido. Es el default.
 *   • `imagen` / `video` — el usuario SÍ eligió, y el agente lo asume desde el primer
 *     mensaje.
 *
 * Sigue sin aceptar `null`, al revés que los otros cuatro campos: `agente` no es "sin
 * preferencia escondida" sino una posición que se VE en el switch, que es lo que el ADR
 * anterior pedía. Lo que el mensaje pida explícitamente gana en las tres.
 */
export type TipoDeSalida = 'agente' | 'imagen' | 'video';

export interface PreferenciasDeGeneracion {
  /**
   * Agente, imagen o video: lo que el usuario tiene elegido en el switch de la caja.
   * Decide QUÉ pastillas se ven —la duración es sólo de video, y en `agente` no se ve
   * ninguna de las dos mitades— y viaja al prompt para que el agente sepa a qué apunta el
   * pedido desde el primer mensaje, sin tener que adivinarlo de una frase que muchas
   * veces no lo dice ("algo para el feed del jueves").
   *
   * Sin `null` a propósito: el estado "que decida el agente" es una posición VISIBLE del
   * switch y no un tercer valor escondido. Lo que el mensaje pida explícitamente gana.
   */
  tipoDeSalida: TipoDeSalida;
  /** Slug de `FORMAT_OPTIONS_V2` (post, story, square…) o null = sin preferencia. */
  formatoImagen: string | null;
  /** 1K / 2K / 4K o null = la base (1K). */
  calidadImagen: ResolucionImagen | null;
  /** Slug de formato de video (los que acepta `generate_video`) o null = sin preferencia. */
  formatoVideo: string | null;
  /** Estándar (`basica`) o Profesional, o null = lo que el agente decida (profesional). */
  calidadVideo: CalidadVideo | null;
  /** Segundos del video, o null = lo que decida el agente (5). Tope según la calidad. */
  duracionVideo: number | null;
  /**
   * Resolución del video (480p / 720p / 1080p), o null = la del motor (720p).
   *
   * Es la misma que el Creador de video ofrece en su ruedita y la que `generate_video`
   * acepta en `resolution` (ADR-2026-09-24-la-resolucion-de-video-tambien-es-una-preferencia).
   * Multiplica el costo por segundo, así que viaja al prompt con su precio al lado.
   */
  resolucionVideo: VideoResolution | null;
  /**
   * TIPO de video (slug de `video_tipos`: reseña, unboxing, producto héroe…), o null =
   * **General**, que no es un tipo sino su ausencia: ahí el escritor de prompts del
   * backend elige por lo que se pide y lo que se adjunta
   * (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
   *
   * Es la misma elección que la pill del Creador de video, ahora también en la caja del
   * Agente cuando el switch está en Video
   * (ADR-2026-09-22-el-tipo-de-video-tambien-en-la-caja-del-agente). Con un tipo puesto,
   * el backend escribe el prompt guiado por la skill de ESE tipo.
   */
  tipoVideo: string | null;
}

export const PREFERENCIAS_POR_DEFECTO: PreferenciasDeGeneracion = {
  tipoDeSalida: 'agente',
  formatoImagen: null,
  calidadImagen: null,
  formatoVideo: null,
  calidadVideo: null,
  duracionVideo: null,
  resolucionVideo: null,
  tipoVideo: null,
};

export const CLAVE_PREFERENCIAS = 'atlas-chat-atlas-preferencias-de-generacion';

/** Los formatos de imagen: los del catálogo, con su nombre humano. */
export const FORMATOS_IMAGEN = FORMAT_OPTIONS_V2.map((f) => ({ id: f.id, etiqueta: f.name }));

/** Los formatos que acepta `generate_video` (su contrato lista estos cinco). */
export const SLUGS_FORMATO_VIDEO = ['post', 'square', 'story', 'portrait', 'banner'] as const;
export const FORMATOS_VIDEO = FORMATOS_IMAGEN.filter((f) =>
  (SLUGS_FORMATO_VIDEO as readonly string[]).includes(f.id),
);

/** Las calidades de imagen, con lo que cuestan (espejo de `resolucionImagen.ts`). */
export const CALIDADES_IMAGEN: { id: ResolucionImagen; etiqueta: string; detalle: string }[] = [
  { id: '1K', etiqueta: '1080p', detalle: '1 crédito' },
  { id: '2K', etiqueta: '2K', detalle: '2 créditos' },
  { id: '4K', etiqueta: '4K', detalle: '3 créditos' },
];

/** Las dos posiciones del switch de la caja, con el nombre que ve el usuario. */
/** Las tres posiciones del switch, en el orden en que se ven. `agente` va PRIMERO: es el
 *  default y es el que no obliga a decidir nada antes de escribir. */
export const TIPOS_DE_SALIDA: { id: TipoDeSalida; etiqueta: string }[] = [
  { id: 'agente', etiqueta: 'Agente' },
  { id: 'imagen', etiqueta: 'Imagen' },
  { id: 'video', etiqueta: 'Video' },
];

/**
 * Las resoluciones de video, con lo que cuestan por segundo. Las mismas tres que el
 * Creador de video y las que acepta `generate_video`; el precio cambia con la calidad
 * (Profesional 2/3/5, Estándar 1/2/4 créditos por segundo), así que el detalle dice las
 * dos: en una lista de tres opciones, el número es la razón para elegir.
 */
export const RESOLUCIONES_VIDEO: { id: VideoResolution; etiqueta: string; detalle: string }[] =
  LISTA_DE_RESOLUCIONES.map((id) => ({
    id,
    etiqueta: id,
    // En 480p Profesional dice además "Borrador" (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p).
    detalle: `${detalleDeResolucion('profesional', id)} · ${videoGeneralRatePerSec('standard', id)} en Estándar`,
  }));

/** Las calidades de video, con el nombre que ve el usuario. */
export const CALIDADES_VIDEO: { id: CalidadVideo; etiqueta: string; detalle: string }[] = [
  { id: 'basica', etiqueta: 'Estándar', detalle: 'más barata, hasta 15 s' },
  { id: 'profesional', etiqueta: 'Profesional', detalle: 'mejor modelo, hasta 30 s' },
];

/**
 * Los segundos van de a UNO, con un slider, y no en cuatro saltos
 * (ADR-2026-09-21-la-duracion-se-elige-al-segundo). La primera versión de la pastilla
 * ofrecía 5 / 10 / 15 / 30: para un default alcanzaba, pero un video de 8 segundos —el
 * largo real de la mayoría de los reels— no se podía dejar puesto, y la card de video, que
 * es la MISMA preferencia vista de cerca, siempre tuvo el slider al segundo. Con cuatro
 * pastillas, un 8 puesto desde el agente no coincidía con ninguna y la fila se veía sin
 * nada elegido.
 */
export const DURACION_MINIMA = 1;

/** Los segundos que corre el motor cuando nadie eligió nada (el default de `generate_video`). */
export const DURACION_POR_DEFECTO = 5;

/** Hasta cuántos segundos llega esa calidad (sin calidad elegida corre Profesional, que
 *  es el default del agente). Mismos topes que el slider de la card. */
export const topeDeDuracion = (calidad: CalidadVideo | null): number =>
  calidad === 'basica' ? VIDEO_BASICA_MAX_S : VIDEO_PROFESIONAL_MAX_S;

const esTipoDeSalida = (v: unknown): v is TipoDeSalida =>
  v === 'agente' || v === 'imagen' || v === 'video';
const esFormatoImagen = (v: unknown): v is string =>
  typeof v === 'string' && FORMATOS_IMAGEN.some((f) => f.id === v);
/** ¿Ese slug lo acepta `generate_video`? El picker de formatos ofrece TODOS los de imagen,
 *  y sólo cinco valen para video. */
export const esFormatoVideo = (v: unknown): v is string =>
  typeof v === 'string' && (SLUGS_FORMATO_VIDEO as readonly string[]).includes(v);
const esCalidadVideo = (v: unknown): v is CalidadVideo => v === 'basica' || v === 'profesional';
const esCalidadImagen = (v: unknown): v is ResolucionImagen =>
  typeof v === 'string' && (RESOLUCIONES as readonly string[]).includes(v);
/**
 * Un slug de tipo de video. NO se valida contra el catálogo: los tipos viven en la tabla
 * `video_tipos` y esto es un módulo puro que corre antes de cualquier consulta — uno
 * nuevo en la base tiene que funcionar sin tocar acá. Se valida la FORMA, y un slug que
 * ya no exista se lee "General" en la pill (`rotuloDePill`) y lo ignora el backend.
 *
 * `general` no es un tipo: es su ausencia, así que se guarda como `null`.
 */
const esTipoVideo = (v: unknown): v is string =>
  typeof v === 'string' && v !== 'general' && /^[a-z0-9-]{1,40}$/.test(v);

/** Una de las tres resoluciones que acepta el motor de video. */
const esResolucionVideo = (v: unknown): v is VideoResolution =>
  typeof v === 'string' && RESOLUCIONES_VIDEO.some((r) => r.id === v);

/** Un entero de segundos dentro del rango que acepta el motor. */
const esDuracion = (v: unknown): v is number =>
  typeof v === 'number' &&
  Number.isInteger(v) &&
  v >= DURACION_MINIMA &&
  v <= VIDEO_PROFESIONAL_MAX_S;

/** Sanea cualquier cosa a preferencias válidas: lo que no se entiende cae a null. */
export function normalizarPreferencias(entrada: unknown): PreferenciasDeGeneracion {
  const o = (entrada && typeof entrada === 'object' ? entrada : {}) as Record<string, unknown>;
  const calidadVideo = esCalidadVideo(o.calidadVideo) ? o.calidadVideo : null;
  return {
    // Lo guardado antes de que el switch existiera no lo trae: arranca en Agente, que es
    // el default y el que no asume nada. Lo guardado CON el switch viejo sí lo trae
    // ('imagen' o 'video') y se respeta: a quien ya eligió no se le cambia la posición.
    tipoDeSalida: esTipoDeSalida(o.tipoDeSalida) ? o.tipoDeSalida : PREFERENCIAS_POR_DEFECTO.tipoDeSalida,
    formatoImagen: esFormatoImagen(o.formatoImagen) ? o.formatoImagen : null,
    calidadImagen: esCalidadImagen(o.calidadImagen) ? normalizarResolucion(o.calidadImagen) : null,
    formatoVideo: esFormatoVideo(o.formatoVideo) ? o.formatoVideo : null,
    calidadVideo,
    // Recortada al tope de la calidad: bajar a Estándar con 30 s puestos tiene que dejar
    // 15 s, no un pedido que el motor va a clampear por su cuenta más adelante.
    duracionVideo: esDuracion(o.duracionVideo)
      ? Math.min(o.duracionVideo, topeDeDuracion(calidadVideo))
      : null,
    resolucionVideo: esResolucionVideo(o.resolucionVideo) ? o.resolucionVideo : null,
    tipoVideo: esTipoVideo(o.tipoVideo) ? o.tipoVideo : null,
  };
}

/**
 * ── EL RESETEO DE UNA SOLA VEZ ────────────────────────────────────────────────
 *
 * La versión del formato guardado. Lo guardado con una versión vieja **vuelve a
 * `tipoDeSalida` por defecto una sola vez** y conserva el resto de las preferencias
 * (ADR-2026-09-22-el-switch-arranca-en-agente).
 *
 * Existe porque el switch **recuerda** la posición y eso vive en `localStorage`, o sea
 * POR DISPOSITIVO: al estrenar la posición `agente` como default, el escritorio que
 * nunca lo había tocado abría en Agente y el teléfono que sí seguía abriendo en Video,
 * y se leía como que el default no funcionaba en el teléfono.
 *
 * No alcanzaba con cambiar `CLAVE_PREFERENCIAS`: eso borra también el formato, la
 * calidad y la duración que el usuario dejó puestos, que no tienen nada que ver.
 * Versionar adentro resetea sólo el campo que cambió de default.
 *
 * Después del reseteo el switch vuelve a recordar como siempre: quien elija Video lo va
 * a encontrar en Video la próxima vez.
 */
export const VERSION_PREFERENCIAS = 2;

/** Lo guardado, saneado y con el reseteo de versión aplicado si hacía falta. Puro: el
 *  que decide si hay que volver a escribirlo es `leerPreferencias`. */
export function migrarPreferencias(entrada: unknown): PreferenciasDeGeneracion {
  const o = (entrada && typeof entrada === 'object' ? entrada : {}) as Record<string, unknown>;
  const p = normalizarPreferencias(o);
  return o.v === VERSION_PREFERENCIAS
    ? p
    : { ...p, tipoDeSalida: PREFERENCIAS_POR_DEFECTO.tipoDeSalida };
}

export function leerPreferencias(storage: StorageLike | null | undefined): PreferenciasDeGeneracion {
  if (!storage) return PREFERENCIAS_POR_DEFECTO;
  try {
    const crudo = storage.getItem(CLAVE_PREFERENCIAS);
    if (!crudo) return PREFERENCIAS_POR_DEFECTO;
    const migradas = migrarPreferencias(JSON.parse(crudo));
    // Se vuelve a escribir con la versión puesta: sin esto el reseteo se repetiría en
    // cada carga y el switch no volvería a recordar nunca.
    guardarPreferencias(storage, migradas);
    return migradas;
  } catch {
    return PREFERENCIAS_POR_DEFECTO;
  }
}

export function guardarPreferencias(
  storage: StorageLike | null | undefined,
  preferencias: PreferenciasDeGeneracion,
): void {
  if (!storage) return;
  try {
    storage.setItem(
      CLAVE_PREFERENCIAS,
      JSON.stringify({ ...normalizarPreferencias(preferencias), v: VERSION_PREFERENCIAS }),
    );
  } catch {
    /* storage lleno o bloqueado: la preferencia vive lo que dure la sesión */
  }
}

/** ¿Hay alguna preferencia puesta? El TIPO DE SALIDA no cuenta: siempre tiene un valor
 *  (el switch nace en Agente), así que contarlo daría "sí" siempre y el nombre mentiría. */
export const hayPreferencias = (p: PreferenciasDeGeneracion): boolean =>
  p.formatoImagen !== null ||
  p.calidadImagen !== null ||
  p.formatoVideo !== null ||
  p.calidadVideo !== null ||
  p.duracionVideo !== null ||
  p.tipoVideo !== null;

const nombreDeFormato = (slug: string): string =>
  FORMATOS_IMAGEN.find((f) => f.id === slug)?.etiqueta ?? slug;

/**
 * El párrafo del bloque volátil del system prompt.
 *
 * Sale SIEMPRE, tenga o no preferencias puestas
 * (ADR-2026-09-13-el-agente-configura-las-preferencias). Antes se omitía cuando estaban
 * todas en null y eso dejaba al agente sin saber que la opción existía: no podía
 * contestar "¿en qué calidad generás?" ni ofrecer cambiarla, justo en el caso que más
 * lo necesita — con la confirmación apagada la card no aparece y la preferencia es el
 * ÚNICO lugar donde se elige la calidad.
 *
 * Los campos sin preferencia se nombran igual, con el default que de verdad corre, para
 * que el modelo no invente que "no hay calidad" cuando lo que hay es 1K.
 */
export function describirPreferencias(p: PreferenciasDeGeneracion): string {
  const imagen: string[] = [];
  if (p.formatoImagen) imagen.push(`formato "${p.formatoImagen}" (${nombreDeFormato(p.formatoImagen)})`);
  if (p.calidadImagen) {
    const c = CALIDADES_IMAGEN.find((x) => x.id === p.calidadImagen);
    imagen.push(`calidad ${c?.etiqueta ?? p.calidadImagen} (${c?.detalle ?? ''} por imagen; el sistema ya la manda, no hace falta que la pidas)`);
  }
  const video: string[] = [];
  if (p.formatoVideo) video.push(`formato "${p.formatoVideo}" (${nombreDeFormato(p.formatoVideo)})`);
  if (p.calidadVideo) {
    const c = CALIDADES_VIDEO.find((x) => x.id === p.calidadVideo);
    video.push(`calidad "${p.calidadVideo}" (${c?.etiqueta ?? ''})`);
  }
  if (p.resolucionVideo) {
    const r = RESOLUCIONES_VIDEO.find((x) => x.id === p.resolucionVideo);
    video.push(`resolución ${p.resolucionVideo} (${r?.detalle ?? ''}; mandala en \`resolution\`)`);
  }
  if (p.duracionVideo) video.push(`duración ${p.duracionVideo} segundos`);
  // El TIPO va con su slug: es el nombre de la skill con la que el backend escribe el
  // prompt, y el nombre lindo vive en la base (este módulo es puro). Sin tipo no se
  // nombra: "General" ya está dicho en la línea de "sin preferencia".
  if (p.tipoVideo) video.push(`tipo "${p.tipoVideo}" (el sistema escribe el prompt guiado por ese tipo; lo eligió él en la pastilla de la caja y vos no lo cambiás)`);
  const lineas = [
    'PREFERENCIAS DEL USUARIO PARA GENERAR (las elige en las pastillas de la caja —Formato, Calidad, Duración— y en el menú "+" → Preferencias de contenido; vos las cambiás con `configurar_preferencias` cuando te lo pide): son los valores POR DEFECTO. Usalas salvo que en el mensaje pida otra cosa, y en ese caso ganá lo que pide.',
  ];
  // El switch va PRIMERO y con su propia línea: no es un default más, es a qué apunta
  // todo el pedido (ADR-2026-09-21-el-switch-de-imagen-o-video).
  lineas.push(
    p.tipoDeSalida === 'video'
      ? '• Switch de la caja: VIDEO. Lo que pida se resuelve con generate_video salvo que pida claramente una imagen. Si el pedido no dice cuál de los dos es, es video.'
      : p.tipoDeSalida === 'imagen'
        ? '• Switch de la caja: IMAGEN. Lo que pida se resuelve con generate_image (o un lote) salvo que pida claramente un video. Si el pedido no dice cuál de los dos es, es imagen.'
        : '• Switch de la caja: AGENTE. El usuario no eligió: decidís vos si el pedido va a imagen o a video. Si no se deduce, preguntale en una línea antes de generar.',
  );
  lineas.push(
    imagen.length
      ? `• Imagen: ${imagen.join(' · ')}.`
      : '• Imagen: sin preferencia (formato: el último que usó · calidad Estándar, 1 crédito por imagen).',
  );
  lineas.push(
    video.length
      ? `• Video: ${video.join(' · ')}.`
      : '• Video: sin preferencia (formato "story" · calidad Profesional · 720p · 5 segundos).',
  );
  if (p.formatoImagen) lineas.push('Con preferencia de formato de imagen, ésta manda sobre el "último formato usado".');
  return lineas.join('\n');
}

// ── Cambiarlas hablando (la tool `configurar_preferencias`) ─────────────────

/** Lo que el modelo puede mandar para volver un campo a "sin preferencia". */
export const SIN_PREFERENCIA = 'ninguna';

export interface CambioDePreferencias {
  /** Las preferencias que hay que guardar. Siempre válidas. */
  siguiente: PreferenciasDeGeneracion;
  /** Qué cambió de verdad, en castellano, para que el agente lo cuente sin inventar. */
  cambios: string[];
  /** Qué se pidió y no existe, con los valores válidos al lado. */
  rechazos: string[];
}

const CAMPOS: Array<{
  clave: keyof PreferenciasDeGeneracion;
  nombre: string;
  validos: string[];
  /** Cómo se lee el valor elegido (la etiqueta humana, no el slug). */
  etiqueta: (v: string) => string;
  /** Campo SIN "sin preferencia": mandar `null` o "ninguna" se rechaza en vez de borrarlo
   *  (el switch de imagen/video siempre está en una posición). */
  obligatorio?: boolean;
  /** Segundos: el valor es un número y el tope depende de la calidad ya aplicada. */
  numerico?: boolean;
}> = [
  {
    clave: 'tipoDeSalida',
    nombre: 'Tipo de contenido',
    validos: TIPOS_DE_SALIDA.map((t) => t.id),
    etiqueta: (v) => TIPOS_DE_SALIDA.find((t) => t.id === v)?.etiqueta ?? v,
    obligatorio: true,
  },
  {
    clave: 'formatoImagen',
    nombre: 'Formato de imagen',
    validos: FORMATOS_IMAGEN.map((f) => f.id),
    etiqueta: nombreDeFormato,
  },
  {
    clave: 'calidadImagen',
    nombre: 'Calidad de imagen',
    validos: [...RESOLUCIONES],
    etiqueta: (v) => {
      const c = CALIDADES_IMAGEN.find((x) => x.id === v);
      return c ? `${c.etiqueta} (${c.detalle})` : v;
    },
  },
  {
    clave: 'formatoVideo',
    nombre: 'Formato de video',
    validos: [...SLUGS_FORMATO_VIDEO],
    etiqueta: nombreDeFormato,
  },
  {
    clave: 'calidadVideo',
    nombre: 'Calidad de video',
    validos: CALIDADES_VIDEO.map((c) => c.id),
    etiqueta: (v) => CALIDADES_VIDEO.find((c) => c.id === v)?.etiqueta ?? v,
  },
  {
    clave: 'resolucionVideo',
    nombre: 'Resolución del video',
    validos: RESOLUCIONES_VIDEO.map((r) => r.id),
    etiqueta: (v) => {
      const r = RESOLUCIONES_VIDEO.find((x) => x.id === v);
      return r ? `${r.etiqueta} (${r.detalle})` : v;
    },
  },
  // ÚLTIMA a propósito: su tope sale de la calidad, y en el mismo pedido pueden venir las
  // dos ("videos básicos de 15 segundos"). Al procesarse después, el tope ya es el nuevo.
  {
    clave: 'duracionVideo',
    nombre: 'Duración del video',
    // Vacío: la rama numérica arma su propio mensaje de rechazo con el tope vivo (que
    // depende de la calidad), no con una lista fija.
    validos: [],
    etiqueta: (v) => `${v} segundos`,
    numerico: true,
  },
];

/**
 * Aplica lo que pidió el agente sobre las preferencias que hay.
 *
 * Existe en vez de pasar el pedido por `normalizarPreferencias` porque esa función cae a
 * `null` en silencio: pedir "8K" dejaba al usuario generando en Estándar convencido de
 * que había cambiado algo, y al agente diciéndole que sí. Acá lo inválido se RECHAZA y
 * el motivo viaja de vuelta con los valores que sí existen.
 *
 * Un campo AUSENTE no se toca; un campo en `null` (o en `"ninguna"`, que es lo que puede
 * mandar un enum de string) vuelve a "sin preferencia".
 */
export function aplicarCambioDePreferencias(
  actual: PreferenciasDeGeneracion,
  pedido: unknown,
): CambioDePreferencias {
  const siguiente: PreferenciasDeGeneracion = { ...normalizarPreferencias(actual) };
  const cambios: string[] = [];
  const rechazos: string[] = [];
  const p = (pedido && typeof pedido === 'object' ? pedido : {}) as Record<string, unknown>;

  for (const campo of CAMPOS) {
    if (!(campo.clave in p)) continue;
    const crudo = p[campo.clave];
    const anterior = siguiente[campo.clave];

    // Volver a "sin preferencia".
    if (crudo === null || crudo === SIN_PREFERENCIA) {
      if (campo.obligatorio) {
        rechazos.push(
          `${campo.nombre}: no tiene "${SIN_PREFERENCIA}" — siempre es uno de ${campo.validos.join(' / ')}.`,
        );
        continue;
      }
      if (anterior !== null) {
        siguiente[campo.clave] = null as never;
        cambios.push(`${campo.nombre}: sin preferencia (vuelve al default)`);
      }
      continue;
    }
    if (crudo === undefined) continue;

    // Los segundos son un NÚMERO con tope vivo (la calidad que ya quedó aplicada), así
    // que no salen de la lista de válidos: "8" es tan legítimo como los cuatro saltos que
    // ofrece la pastilla, y 30 deja de serlo apenas la calidad es básica.
    if (campo.numerico) {
      const n = Math.round(Number(crudo));
      const tope = topeDeDuracion(siguiente.calidadVideo);
      if (!Number.isFinite(n) || n < DURACION_MINIMA || n > tope) {
        rechazos.push(
          `${campo.nombre}: "${String(crudo)}" no entra. Van de ${DURACION_MINIMA} a ${tope} segundos con la calidad ${
            siguiente.calidadVideo === 'basica' ? 'Estándar' : 'Profesional'
          }, o "${SIN_PREFERENCIA}".`,
        );
        continue;
      }
      if (n === anterior) continue;
      siguiente[campo.clave] = n as never;
      cambios.push(`${campo.nombre}: ${campo.etiqueta(String(n))}`);
      continue;
    }

    const valor = String(crudo).trim();
    // La resolución se escribe "2k" tan seguido como "2K"; el resto son slugs en minúscula.
    const normalizado =
      campo.clave === 'calidadImagen' ? valor.toUpperCase() : valor.toLowerCase();
    if (!campo.validos.includes(normalizado)) {
      rechazos.push(
        `${campo.nombre}: "${valor}" no existe. Válidos: ${campo.validos.join(', ')} o "${SIN_PREFERENCIA}".`,
      );
      continue;
    }
    if (normalizado === anterior) continue;
    siguiente[campo.clave] = normalizado as never;
    cambios.push(`${campo.nombre}: ${campo.etiqueta(normalizado)}`);
  }

  // Bajar la calidad con una duración más larga puesta la recorta: si no, queda guardada
  // una preferencia que el motor va a clampear sin decírselo a nadie.
  const tope = topeDeDuracion(siguiente.calidadVideo);
  if (siguiente.duracionVideo !== null && siguiente.duracionVideo > tope) {
    siguiente.duracionVideo = tope;
    cambios.push(`Duración del video: ${tope} segundos (es el tope de esa calidad)`);
  }

  return { siguiente, cambios, rechazos };
}
